import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Form, Input, Select, Button, Divider, message } from 'antd';
import { SaveOutlined, CloseOutlined, ApartmentOutlined, ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import { departmentsApi } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';

const { Option } = Select;

const EditDepartment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/departments', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [currentDepartment, setCurrentDepartment] = useState(null);

  useEffect(() => {
    fetchDepartment();
    fetchDepartments();
  }, [id]);

  const fetchDepartment = async () => {
    setFetching(true);
    try {
      const response = await departmentsApi.getById(id);
      const department = response.data;
      setCurrentDepartment(department);
      form.setFieldsValue({
        team_name: department.team_name,
        parent_id: department.parent_id,
      });
    } catch (error) {
      console.error('Error fetching department:', error);
      message.error('خطا در دریافت اطلاعات دپارتمان');
      goBackToList();
    } finally {
      setFetching(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentsApi.getAll();
      const filtered = (response.data || []).filter((dept) => dept.id !== parseInt(id, 10));
      setDepartments(filtered);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('خطا در دریافت لیست دپارتمان‌ها');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const validatedValues = await form.validateFields();
      setLoading(true);
      const payload = {
        team_name: validatedValues.team_name,
        parent_id: validatedValues.parent_id || null,
      };

      await departmentsApi.update(id, payload);
      message.success('دپارتمان با موفقیت ویرایش شد');
      goBackToList();
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else {
        console.error('Error updating department:', error);
        message.error(error?.message || 'خطا در ویرایش دپارتمان');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    goBackToList();
  };

  if (fetching) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
            <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                  <EditOutlined className="text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش دپارتمان</h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">در حال بارگذاری...</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
              <FormSkeleton />
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0  " bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                <EditOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش دپارتمان</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  {currentDepartment ? `در حال ویرایش: ${currentDepartment.team_name}` : 'ویرایش اطلاعات دپارتمان'}
                </p>
              </div>
            </div>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={goBackToList}
              className="flex items-center gap-2"
              style={{ fontFamily: 'CustomFont' }}
            >
              بازگشت
            </Button>
          </div>

          {/* Form */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="border border-gray-100 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                style={{ fontFamily: 'CustomFont' }}
                size="middle"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    label="نام دپارتمان"
                    name="team_name"
                    rules={[
                      { required: true, message: 'لطفا نام دپارتمان را وارد کنید' },
                      { min: 2, message: 'نام دپارتمان باید حداقل 2 کاراکتر باشد' },
                      { max: 100, message: 'نام دپارتمان نمی‌تواند بیشتر از 100 کاراکتر باشد' },
                    ]}
                  >
                    <Input
                      placeholder="مثال: فروش، بازاریابی، پشتیبانی..."
                      prefix={<ApartmentOutlined className="text-gray-400" />}
                    />
                  </Form.Item>

                  <Form.Item
                    label="دپارتمان والد"
                    name="parent_id"
                    tooltip="در صورت انتخاب، این دپارتمان زیرمجموعه دپارتمان انتخاب‌شده خواهد بود"
                  >
                    <Select
                      placeholder="انتخاب دپارتمان والد (اختیاری)"
                      allowClear
                      showSearch
                      loading={loading}
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {departments.map((dept) => (
                        <Option key={dept.id} value={dept.id}>
                          {dept.team_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <Divider />

                <div className="flex items-center justify-end gap-3">
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancel}
                    style={{ minWidth: '110px' }}
                  >
                    لغو
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={loading}
                    className="bg-blue-600 hover:bg-blue-700 border-0"
                    style={{ minWidth: '140px' }}
                  >
                    ذخیره تغییرات
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditDepartment;
